import Component, { mixins } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'

import WithRender from './FicheRepondantForm.html'

import {
  FormGroup,
  FormControl,
  MAdd,
  MDropdown,
  MDropdownItem,
  MFileUpload,
  MForm,
  MMessage,
  MMessageState,
  MTextfield,
  MTextfieldType,
  RequiredValidator,
  AbstractControl,
  EmailValidator
} from '@ulaval/modul-components'
import { MFile } from '@ulaval/modul-components/dist/utils/file/file'

import { STORE_GETTERS } from '@/store'
import { ICountry } from '@/modules/global/localization/country/ICountry.model'
import { FICHE_REPONDANT_LANGUE, IFicheRepondantModel } from '../FicheRepondant.model'
import { IFileModel, IFileUploadModel } from '@/modules/global/file/File.model'
import UploadedFileRow from '@/modules/global/file/UploadedFileRow'

export interface IFicheRepondantFormPayload {
  ville: string;
  idPays: number;
  nom: string;
  prenom: string;
  fonction: string;
  telephone: string;
  courriel: string;
  langue: FICHE_REPONDANT_LANGUE;
  employeurNom: string;
  employeurAdresse: string;
  employeurPreuveUrl: string;
  employeurPreuveFichiers: Array<IFileUploadModel<IFileModel>>;
}

@WithRender
@Component({
  components: {
    MAdd,
    MDropdown,
    MDropdownItem,
    MFileUpload,
    MForm,
    MMessage,
    MTextfield,
    UploadedFileRow
  }
})
export default class FicheRepondantForm extends mixins() {
  public formGroup: FormGroup = new FormGroup({
    employeurNom: new FormControl<string>([RequiredValidator()]),
    employeurAdresse: new FormControl<string>([]),
    employeurPreuveUrl: new FormControl<string>([]),
    prenom: new FormControl<string>([RequiredValidator()]),
    nom: new FormControl<string>([RequiredValidator()]),
    fonction: new FormControl<string>([RequiredValidator()]),
    courriel: new FormControl<string>([RequiredValidator(), EmailValidator()]),
    country: new FormControl<number>([RequiredValidator()]),
    ville: new FormControl<string>([RequiredValidator()]),
    telephone: new FormControl<string>([]),
    langue: new FormControl<FICHE_REPONDANT_LANGUE>([RequiredValidator()]),
  })
  private fichiersPreuve: Array<IFileUploadModel<IFileModel>> = []
  private formErrors: { [key: string]: boolean } = {}

  private readonly MTextfieldType: object = MTextfieldType
  private readonly MMessageState: object = MMessageState
  private readonly FICHE_REPONDANT_LANGUE: object = FICHE_REPONDANT_LANGUE
  private readonly ALLOWED_ATTACHMENT_EXTENSIONS: Array<string> = [
    'pdf',
    'png',
    'jpg',
    'jpeg'
  ]

  private readonly ERROR_KEY_PREUVE_EMPLOYEUR_REQUIS: string = 'preuve-employeur-required'

  @Prop()
  private ficheRepondant!: IFicheRepondantModel | null

  public get employeurNomField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('employeurNom');
  }

  public get employeurAdresseField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('employeurAdresse');
  }

  public get employeurPreuveUrlField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('employeurPreuveUrl');
  }

  public get prenomField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('prenom');
  }

  public get nomField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('nom');
  }

  public get fonctionField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('fonction');
  }

  public get courrielField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('courriel');
  }

  public get villeField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('ville');
  }

  public get telephoneField(): AbstractControl<string> {
    return this.formGroup.getControl<string>('telephone');
  }

  public get countryDropdown(): AbstractControl<number> {
    return this.formGroup.getControl<number>('country');
  }

  public get langueDropdown(): AbstractControl<FICHE_REPONDANT_LANGUE> {
    return this.formGroup.getControl<FICHE_REPONDANT_LANGUE>('langue');
  }

  private get countries(): Array<ICountry> {
    return this.$store.getters[STORE_GETTERS.COUNTRY_GET_COUNTRIES]
  }

  @Watch('ficheRepondant', { immediate: true })
  private onFicheRepondantUpdate(): void {
    this.populateFormFromFicheRepondant()
  } 

  public async getFormPayload(): Promise<IFicheRepondantFormPayload | null> {
    await this.validate()
    
    if (this.formGroup.hasErrorDeep() || Object.keys(this.formErrors).length > 0) {
      return null
    }
    
    return {
      ville: this.villeField.value,
      idPays: this.countryDropdown.value,
      nom: this.nomField.value,
      prenom: this.prenomField.value,
      fonction: this.fonctionField.value,
      telephone: this.telephoneField.value,
      courriel: this.courrielField.value,
      langue: this.langueDropdown.value,
      employeurNom: this.employeurNomField.value,
      employeurAdresse: this.employeurAdresseField.value,
      employeurPreuveUrl: this.employeurPreuveUrlField.value,
      employeurPreuveFichiers: this.fichiersPreuve
    }
  } 

  private async validate(): Promise<void> {
    this.formErrors = {}

    await this.formGroup.submit()
  }

  private onFilesReady(files: Array<MFile>): void {
    this.$file.uploadTemp(files)
  }

  private onFilesUploadCompleted(files: Array<MFile>): void {
    files.forEach((file: MFile) => {
      this.fichiersPreuve.push({ file })
    })
  }

  private removePreuveFicher(index: number): void {
    this.$delete(this.fichiersPreuve, index)
  }

  private populateFormFromFicheRepondant(): void {
    if (this.ficheRepondant) {
      this.villeField.value = this.ficheRepondant.ville
      this.countryDropdown.value = this.ficheRepondant.pays.id
      this.nomField.value = this.ficheRepondant.nom
      this.prenomField.value = this.ficheRepondant.prenom
      this.fonctionField.value = this.ficheRepondant.fonction
      this.telephoneField.value = this.ficheRepondant.telephonePrincipal
      this.courrielField.value = this.ficheRepondant.courriel
      this.langueDropdown.value = this.ficheRepondant.langue
      this.employeurNomField.value = this.ficheRepondant.employeurNom
      this.employeurPreuveUrlField.value = this.ficheRepondant.employeurPreuveUrl
      this.employeurAdresseField.value = this.ficheRepondant.employeurAdresse
      
      this.ficheRepondant.employeurPreuveFichiers.forEach((fichier: IFileModel) => {
        this.fichiersPreuve.push({
          server_file: fichier
        })
      })
    }
  }
}
